// Источник задач для тренажёра «Зевки»: свои позиции из кэша разборов +
// пазлы Lichess когда свои кончились. Чистая логика (сбор/верификация) без DOM —
// покрыта vitest. Движок инжектится в verifyAttempt (DI) → тест на моке.
import { Chess } from 'chess.js';
import { uciToSan, cpWhiteFromAnalysis, type EngineAnalysis } from '@ext/core/eval-utils';
import { nodeFen, evalKey } from '@ext/core/evals';
import type { CoachGame } from '@ext/core/game-sources';
import { buildReview, refreshEngineData, type ReviewModel } from '../core/review-model';
import { loadEvals } from './eval-cache';
import { loadArchive } from './blunder-archive';

/** Насколько ход может уступать лучшему и ещё считаться верным (спека V1.1). */
export const TOLERANCE_CP = 30;

export interface OwnPuzzle {
  source: 'own';
  gameId: string; // ключ кэша `${site}:${id}`
  opponent: string;
  ply: number; // индекс полухода в главной линии (для дип-линка)
  moveNo: number; // fullmove номер зевка
  white: boolean; // ходили белые (сторона юзера)
  orientation: 'white' | 'black';
  parentFen: string; // позиция ПЕРЕД зевком
  playedUci: string; // сыгранный плохой ход
  playedSan: string;
  bestUci: string; // лучший ход движка
  bestSan: string;
  bestCpWhite: number | null; // оценка после лучшего (со стороны белых)
  lossCp: number; // Δ: сколько терял ход (со стороны юзера, ≥0)
}

export interface LichessPuzzle {
  source: 'lichess';
  id: string;
  rating: number | null;
  parentFen: string;
  white: boolean;
  orientation: 'white' | 'black';
  bestUci: string; // solution[0]
  bestSan: string;
  bestCpWhite: null;
  solution: string[];
}

export type TrainPuzzle = OwnPuzzle | LichessPuzzle;
export type Verdict = 'best' | 'played' | 'weak';

/** Новая длина серии по вердикту: верный ход продлевает, иначе обнуляет. */
export function nextStreak(current: number, verdict: Verdict): number {
  return verdict === 'best' ? current + 1 : 0;
}

/** Серия после верного решения: чистое решение растит, решение с подсказкой — держит. */
export function streakAfterCorrect(current: number, usedHint: boolean): number {
  return usedHint ? current : nextStreak(current, 'best');
}

/** cp со стороны юзера из cp-со-стороны-белых. */
function moverCp(cpWhite: number | null, white: boolean): number | null {
  if (cpWhite == null) return null;
  return white ? cpWhite : -cpWhite;
}

/** UCI хода из позиции fen по его SAN (или '' если ход нелегален). */
export function sanToUci(fen: string, san: string): string {
  try {
    const c = new Chess();
    c.load(fen, { skipValidation: true });
    const m = c.move(san);
    return m ? `${m.from}${m.to}${m.promotion || ''}` : '';
  } catch {
    return '';
  }
}

/** FEN после применения UCI-хода к позиции (или '' если нелегален). */
export function fenAfterUci(fen: string, uci: string): string {
  try {
    const c = new Chess();
    c.load(fen, { skipValidation: true });
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] });
    return m ? c.fen() : '';
  } catch {
    return '';
  }
}

/** Разбор, восстановленный из кэша: модель + метаданные для сбора пазлов. */
export interface CachedReview {
  gameId: string;
  opponent: string;
  model: ReviewModel;
}

/**
 * Собрать позиции перед зевками/miss юзера из готовых разборов. Отбирает только
 * ходы стороны юзера классов blunder/miss, требует артефакт лучшего хода
 * (иначе позиция пропускается), дедуп по FEN родителя. Ключи из `archived`
 * (стабильный `gameId#ply`) исключаются из ротации навсегда.
 */
export function collectOwnPuzzles(reviews: CachedReview[], archived: Set<string> = new Set()): OwnPuzzle[] {
  const out: OwnPuzzle[] = [];
  const seen = new Set<string>();
  for (const { gameId, opponent, model } of reviews) {
    if (!model.side) continue; // не знаем сторону юзера — атрибуция невозможна
    const userWhite = model.side === 'w';
    for (const mv of model.moves) {
      if (mv.white !== userWhite) continue;
      if (mv.quality !== 'blunder' && mv.quality !== 'miss') continue;
      if (archived.has(`${gameId}#${mv.ply}`)) continue; // в архиве — больше не показываем
      const parent = mv.node.parent;
      if (!parent) continue;
      const bestUci = parent._bestmove;
      if (!bestUci) continue; // нет артефакта лучшего хода → пропускаем
      const parentFen = nodeFen(parent);
      const key = evalKey(parentFen);
      if (seen.has(key)) continue;
      const bestSan = uciToSan(parentFen, bestUci);
      if (!bestSan) continue;
      const playedUci = mv.from && mv.to ? `${mv.from}${mv.to}` : sanToUci(parentFen, mv.san);
      if (!playedUci) continue;
      const beforeMover = moverCp(model.evals.getNode(parent), userWhite);
      const afterMover = moverCp(mv.evalAfterCp, userWhite);
      const lossCp = beforeMover != null && afterMover != null ? Math.max(0, Math.round(beforeMover - afterMover)) : 0;
      seen.add(key);
      out.push({
        source: 'own',
        gameId,
        opponent,
        ply: mv.ply,
        moveNo: mv.moveNo,
        white: mv.white,
        orientation: model.orientation,
        parentFen,
        playedUci,
        playedSan: mv.san,
        bestUci,
        bestSan,
        bestCpWhite: parent._bestCp ?? null,
        lossCp,
      });
    }
  }
  return out;
}

/** Построить разборы из кэша по загруженным партиям (buildReview + loadEvals). */
export function ownReviewsFromGames(games: CoachGame[], nick: string): CachedReview[] {
  const reviews: CachedReview[] = [];
  for (const game of games) {
    let model: ReviewModel;
    try {
      model = buildReview(game, nick);
    } catch {
      continue;
    }
    const gameId = `${game.site}:${game.id}`;
    loadEvals(gameId, model.root, model.evals);
    refreshEngineData(model);
    if (!model.engineReady) continue; // без оценок нечего отбирать
    const opponent = (model.side === 'w' ? model.black : model.white) || '';
    reviews.push({ gameId, opponent, model });
  }
  return reviews;
}

/** Свои пазлы по загруженным партиям одним вызовом (для экрана), минус архив. */
export function ownPuzzlesFromGames(games: CoachGame[], nick: string): OwnPuzzle[] {
  return collectOwnPuzzles(ownReviewsFromGames(games, nick), loadArchive());
}

/**
 * Вердикт по ходу юзера. `best`/`played` определяются без движка; для прочих
 * ходов зовём analyze(fenAfterUser) и сравниваем оценку со стороны юзера с
 * лучшей — уступка ≤ TOLERANCE_CP тоже считается верной.
 */
export async function verifyAttempt(
  puzzle: TrainPuzzle,
  userUci: string,
  analyze: (fen: string) => Promise<EngineAnalysis>,
): Promise<Verdict> {
  if (userUci === puzzle.bestUci) return 'best';
  if (puzzle.source === 'own' && userUci === puzzle.playedUci) return 'played';
  const bestMover = moverCp(puzzle.bestCpWhite, puzzle.white);
  if (bestMover == null) return 'weak'; // нет эталона (пазл Lichess) → строгая проверка
  const after = fenAfterUci(puzzle.parentFen, userUci);
  if (!after) return 'weak';
  let analysis: EngineAnalysis;
  try {
    analysis = await analyze(after);
  } catch {
    return 'weak';
  }
  const userMover = moverCp(cpWhiteFromAnalysis(after, analysis), puzzle.white);
  if (userMover == null) return 'weak';
  return bestMover - userMover <= TOLERANCE_CP ? 'best' : 'weak';
}

interface RawLichess {
  game?: { pgn?: string };
  puzzle?: { id?: string; rating?: number; solution?: string[]; fen?: string; initialPly?: number };
}

/** FEN позиции решателя из PGN: проигрываем ходы включительно до initialPly. */
export function fenFromPgnPly(pgn: string, initialPly: number): string {
  const moves = pgn.trim().split(/\s+/).filter(Boolean);
  const c = new Chess();
  for (let i = 0; i <= initialPly && i < moves.length; i++) {
    try {
      if (!c.move(moves[i]!)) break;
    } catch {
      break;
    }
  }
  return c.fen();
}

/** Нормализовать ответ Lichess puzzle в TrainPuzzle. Возвращает null при кривом теле. */
export function parseLichessPuzzle(raw: RawLichess): LichessPuzzle | null {
  const p = raw?.puzzle;
  const solution = Array.isArray(p?.solution) ? p!.solution!.filter((s) => typeof s === 'string') : [];
  if (!p?.id || solution.length === 0) return null;
  const fen = p.fen || (raw.game?.pgn && p.initialPly != null ? fenFromPgnPly(raw.game.pgn, p.initialPly) : '');
  if (!fen) return null;
  const bestUci = solution[0]!;
  const white = fen.split(/\s+/)[1] !== 'b';
  return {
    source: 'lichess',
    id: p.id,
    rating: typeof p.rating === 'number' ? p.rating : null,
    parentFen: fen,
    white,
    orientation: white ? 'white' : 'black',
    bestUci,
    bestSan: uciToSan(fen, bestUci),
    bestCpWhite: null,
    solution,
  };
}

/** Свежий пазл с открытого API Lichess (без токена). null при сетевой ошибке. */
export async function fetchLichessPuzzle(): Promise<LichessPuzzle | null> {
  try {
    const res = await fetch('https://lichess.org/api/puzzle/next', { credentials: 'omit' });
    if (!res.ok) return null;
    return parseLichessPuzzle((await res.json()) as RawLichess);
  } catch {
    return null;
  }
}
